import { useMemo } from 'react';

import { notification } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useCheckPermissions } from 'hooks/useCheckPermissions';

import * as rivirAlertServices from 'services/rivirAlert';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { useUserInfo } from 'hooks/auth/useUserInfo';

const rivirAlertKeys = {
  all: ['rivir-alerts'],
  lists: () => [...rivirAlertKeys.all, 'list'],
  list: (memberId) => [...rivirAlertKeys.lists(), memberId],
};

export const useRivirAlert = () => {
  const queryClient = useQueryClient();
  const { userInfo } = useUserInfo();

  const hasPermissionManageCard = useCheckPermissions([
    {
      action: ABILITY_ACTION.MAINTAIN,
      subject: ABILITY_SUBJECT.MEMBER_CREDIT_CARD,
    },
  ]);

  const memberId = userInfo?.member?.id;
  const isAlertCreditCard =
    userInfo?.canAddCreditCard &&
    hasPermissionManageCard &&
    !userInfo?.hasCreditCard;

  const creditCardAlert = useMemo(() => {
    return isAlertCreditCard ? [{ type: 'creditCard' }] : [];
  }, [isAlertCreditCard]);

  const queryParams = useMemo(
    () => ({
      pageIndex: 0,
      pageSize: 999,
      memberId: memberId,
      filters: [
        {
          fieldName: 'isRead',
          filterType: 'Equal',
          value: false,
        },
      ],
    }),
    [memberId]
  );

  const { data: alertData, isLoading } = useQuery({
    queryKey: rivirAlertKeys.list(memberId),
    queryFn: () => rivirAlertServices.getNewRivirAlert(queryParams),
    enabled: !!memberId, // Only run if memberId exists
    refetchInterval: 5 * 60 * 1000,
    select: (data) => {
      return data?.data?.gridData || [];
    },
  });

  const alertList = useMemo(() => {
    const fetchData = alertData || [];
    return [...creditCardAlert, ...fetchData];
  }, [creditCardAlert, alertData]);

  const markAsReadMutation = useMutation({
    mutationFn: (alertId) =>
      rivirAlertServices.markRivirAlertAsRead({ alertId }),
    onSuccess: (response) => {
      if (response.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: rivirAlertKeys.list(memberId),
        });
      } else {
        notification.error({ message: response?.message });
      }
    },
    onError: (error) => {
      notification.error({ message: 'Failed to mark alert as read' });
    },
  });

  const reloadAlertList = () => {
    queryClient.invalidateQueries({
      queryKey: rivirAlertKeys.list(memberId),
    });
  };

  const viewAlert = (id, callback) => {
    markAsReadMutation.mutate(id, {
      onSuccess: (response) => {
        if (response.isSuccess && callback) {
          callback();
        }
      },
    });
  };

  return {
    loading: isLoading || markAsReadMutation.isLoading,
    alertList,
    reloadAlertList,
    viewAlert,
  };
};
